import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, FormControlLabel, RadioGroup, Radio } from '@mui/material';
import { AppDispatch, RootState } from '../../../redux/store'; 
import { updateFirmware } from "../../../redux/actions/firmware.actions";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import MDEditor from '@uiw/react-md-editor';
import usePermissionCheck from '../../../hooks/usePermissionCheck';
import ModalShell from "../../../components/common/Modals/ModalShell";
import { clearError } from "../../../redux/reducers/firmware.reducer";

const DESCRIPTION_MAX_LENGTH = 2000;

const UpdateFirmwareInfoModal: React.FC<ModalProps> = ({open, setOpen,object}) => {
    const firmware = object;
    const dispatch = useDispatch<AppDispatch>();
    const { accessToken } = useSelector((state: RootState) => state.user);
    const { canAccess: canAssignAdmin } = usePermissionCheck(['assign_admin_firmware']);

    const { error, loading } = useSelector((state: RootState) => state.firmware);
    const [submitted, setSubmitted] = useState(false);
    const [updateInfo, setUpdateInfo] = useState<any>({});
  
    useEffect(() => {
      if (updateInfo && updateInfo.is_dev && (updateInfo.is_latest || firmware?.is_latest)) {
        setUpdateInfo({...updateInfo, is_latest: false});
      }
    }, [updateInfo.is_dev]);

    const handleSubmit = async (event: React.FormEvent) => {
      if (updateInfo && firmware) {
        await dispatch(updateFirmware({...updateInfo, firmware_id: firmware.id}));
        setSubmitted(true);
      }
    };

    const handleClose = () => {
      dispatch(clearError());
      setUpdateInfo({});
      setSubmitted(false);
      setOpen(false);
    };

    const showForm = !submitted || loading;
    const isDev = updateInfo?.is_dev ?? firmware?.is_dev;
    const descriptionValue = updateInfo?.description??firmware?.description??'';

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size={showForm ? "lg" : "sm"}
      tone={showForm ? undefined : error ? "error" : "success"}
      title={showForm ? "Update firmware" : error ? "An error occurred" : "Firmware updated"}
      subtitle={showForm && firmware ? `${firmware?.type} ${firmware?.version}` : undefined}
      actions={showForm ?
        <>
          <button className="btn" onClick={handleClose}>Close</button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !accessToken ||( updateInfo?.description??firmware?.description??'')===""}
            onClick={handleSubmit}
          >
            {loading ? "Updating…" : "Update firmware info"}
          </button>
        </>
        :
        <button className="btn btn-primary" onClick={handleClose}>OK</button>
      }
    >
      {!showForm ?
        (error ? <p>{error}</p> : <p>Firmware successfully updated.</p>)
        :
        <>
          <div className="dlg-section">
            <span className="dlg-label">Release</span>
            <FormControlLabel
              label="Latest"
              control={
                <Checkbox
                  checked={updateInfo?.is_latest??firmware?.is_latest??false}
                  onChange={(e) => setUpdateInfo({...updateInfo,is_latest:e.target.checked})}
                  disabled={loading || isDev}
                />
              } />
            {isDev && (
              <small className="dlg-muted">Dev firmware cannot be set as latest.</small>
            )}
          </div>

          <div className="dlg-section">
            <span className="dlg-label" id="firmware-visibility-radio-group">Visibility *</span>
            <RadioGroup
              aria-labelledby="firmware-visibility-radio-group"
              defaultValue={firmware?.is_dev ? "dev" : "public"}
              name="visibility-buttons-group"
              row
              onChange={(e)=>{
                const dev = e.target.value==='dev';
                setUpdateInfo({...updateInfo, is_dev: dev, ...(dev ? {} : { is_admin: false })});
              }}
            >
              <FormControlLabel value="public" control={<Radio />} label="Public" />
              <FormControlLabel value="dev" control={<Radio />} label="Dev Only" />
            </RadioGroup>
            {canAssignAdmin && isDev && (
              <FormControlLabel
                label="Admin firmware"
                control={
                  <Checkbox
                    size="small"
                    checked={updateInfo?.is_admin ?? firmware?.is_admin ?? false}
                    disabled={loading}
                    onChange={(e) => setUpdateInfo({...updateInfo, is_admin: e.target.checked})}
                  />
                }
              />
            )}
          </div>

          <div className="dlg-section" data-color-mode={localStorage.getItem('data-theme') ?? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')}>
            <span className="dlg-label">Description *</span>
            <MDEditor value={descriptionValue} onChange={(value) => setUpdateInfo({...updateInfo,description:value})} style={{zIndex:'99'}} textareaProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
            />
            <small className="dlg-muted" style={{ alignSelf: 'flex-end', ...(descriptionValue.length >= DESCRIPTION_MAX_LENGTH ? { color: 'var(--bad)' } : {}) }}>
              {descriptionValue.length}/{DESCRIPTION_MAX_LENGTH}
            </small>
          </div>
        </>
      }
    </ModalShell>
  );
};

export default UpdateFirmwareInfoModal;
